import {createI18n} from 'vue-i18n'
import {getCookie} from "@/shared/lib/utils/cookie";
import {locales, localeFromUrl, type Locale} from "@/shared/lib/i18n";

import jsonRu from '@/shared/locales/ru.json';
import jsonKk from '@/shared/locales/kk.json';
import jsonEn from '@/shared/locales/en.json';

export {locales, type Locale};

let locale: string | null = getCookie('lang') ?? null;

// язык из URL (?lang=kk либо сегмент пути /kk, /promocode/CODE/kk) перебивает cookie
const urlLocale = localeFromUrl(window.location.pathname, window.location.search);
if (urlLocale) {
    locale = urlLocale;
}

type Messages = Partial<typeof jsonRu | typeof jsonKk | typeof jsonEn>;

/**
 * Русская плюрализация: 4 формы вместо стандартных трёх.
 * @param choice {number} индекс выбора, переданный в $tc: `$tc('path.to.rule', choiceIndex)`
 * @param choicesLength {number} общее количество доступных вариантов
 * @returns финальный индекс для выбора соответственного варианта слова
 */
export function russianPluralRule(choice: number, choicesLength: number): number {
    if (choice === 0) {
        return 0;
    }

    const teen = choice > 10 && choice < 20;
    const endsWithOne = choice % 10 === 1;

    if (choicesLength < 4) {
        return (!teen && endsWithOne) ? 1 : 2;
    }
    if (!teen && endsWithOne) {
        return 1;
    }
    if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
        return 2;
    }

    return (choicesLength < 4) ? 2 : 3;
}

const i18n = createI18n<[Messages, Messages, Messages], Locale>({
    inheritLocale: true,
    legacy: false,
    locale: (locale || import.meta.env.VITE_APP_I18N_LOCALE) as Locale,
    // fallbackLocale намеренно не задан. У большинства <i18n>-блоков есть только ru и kk,
    // а английский лежит в глобальном en.json и подхватывается через fallbackRoot.
    // Любой fallbackLocale ломает эту цепочку: локальный ru выигрывает раньше,
    // чем промах уйдёт в глобальный словарь, и на en рендерится русский текст.
    messages: {
        ru: jsonRu,
        kk: jsonKk,
        en: jsonEn,
    },
    pluralRules: {
        'ru': russianPluralRule,
    }
});

// Библиотечный <Auth> из @ismet/vue-ui-kit использует плоские i18n-ключи (login, password…),
// поднимаем уже существующий блок auth.* в top-level. Коллизий с другими top-level ключами нет.
i18n.global.mergeLocaleMessage('ru', jsonRu.auth);
i18n.global.mergeLocaleMessage('kk', jsonKk.auth);
i18n.global.mergeLocaleMessage('en', jsonEn.auth);

/**
 * Смена языка вне компонентов (например, из redirect-функции маршрута).
 * Каст нужен из-за явных type-аргументов createI18n: TS выводит legacy-инстанс,
 * хотя при legacy: false locale — это ref.
 */
export function setGlobalLocale(value: Locale): void {
    (i18n.global.locale as unknown as { value: Locale }).value = value;
}

export default i18n;
